import { useState, useEffect } from "react";
import { useAppDispatch } from "../../app/hook";
import { useParams, useNavigate } from "react-router-dom";
import { showModal } from "../UI/modal/modal-slice";
import {
  SpaceBetween,
  Button,
  ColumnLayout,
  Form,
  Box,
} from "@cloudscape-design/components-themed";
import { MerchantForm } from "./MerchantForm";
import { isNull, isLengthValid } from "../../utils/inputValidation";
import { getMessage } from "../../features/error/messages";
import { COMMON_LABELS, DivisionDict, addBlank } from "../default-constants";
import {
  usePostRateAdjustmentCaseMerchantRegisterMutation,
  usePutRateAdjustmentCaseMerchantUpdateMutation,
  useDeleteRateAdjustmentCaseMerchantMutation,
  useGetDivisionQuery,
} from "../../features/api";
import { getDivisionItems } from "./constants";
import { RateAdjustmentCaseMerchantRequest } from "../../features/api-interfaces";

/*===================================================================================
 * 料率調整案件加盟店登録更新画面
 * 概要：料率調整案件加盟店登録・更新画面を表示し、更新時一覧表表示機能を実装
 *
 * 前の画面からの必要なパラメーター
 * 料率調整案件番号、加盟店枝番（更新時）
 *
 * 遷移先：なし
 * 戻り先：料率調整案件更新画面
 ===================================================================================*/

const Main = (props: any) => {
  const { rateAdjustmentCaseNo, merchantBranchNo } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [postRateAdjustmentCaseMerchantRegister] =
    usePostRateAdjustmentCaseMerchantRegisterMutation();
  const [putRateAdjustmentCaseMerchantUpdate] =
    usePutRateAdjustmentCaseMerchantUpdateMutation();
  const [deleteRateAdjustmentCaseMerchant] =
    useDeleteRateAdjustmentCaseMerchantMutation();

  // 登録画面か更新画面かのフラグ
  const isRegister = !merchantBranchNo;

  // 加盟店名
  const [merchantName, setMerchantName] = useState("");
  const [merchantNameErrorMsg, setMerchantNameErrorMsg] = useState("");
  // サービス名
  const [serviceName, setServiceName] = useState({ value: "", label: "" });
  const [serviceNameErrorMsg, setServiceNameErrorMsg] = useState("");
  // 最終更新情報
  const [updatedBy, setUpdatedBy] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");

  // サービス名一覧
  const [serviceNameList, setServiceNameList] = useState<DivisionDict[]>();

  //戻るボタンの表示確認フラグ（入力フォームに何か入力された場合はtrue）
  const [isFormEntered, setIsFormEntered] = useState(false);

  // 共通データを取得
  // 区分取得API呼び出し(親区分コード：3:契約サービス区分コード)
  const { data: contractServiceKbnItems } = useGetDivisionQuery("3");

  useEffect(() => {
    setServiceNameList(addBlank(getDivisionItems(contractServiceKbnItems)));
  }, [contractServiceKbnItems]);

  //加盟店情報をセット
  useEffect(() => {
    if (props.merchantInfo !== undefined) {
      const service = serviceNameList?.find(
        (item) =>
          item.value === props.merchantInfo.contractServiceCode.toString()
      );
      if (service !== undefined) {
        setServiceName(service);
      }
      setMerchantName(props.merchantInfo.merchantName);
      setUpdatedBy(props.merchantInfo.updatedBy);
      setUpdatedAt(props.merchantInfo.updatedAt);
    }
  }, [props.merchantInfo, serviceNameList]);

  // 加盟店名バリデーションチェック
  const checkMerchantName = (merchantName: string): boolean => {
    //必須チェック
    if (isNull(merchantName)) {
      setMerchantNameErrorMsg(getMessage("KGRERR055").label);
      return false;
    }
    //　文字数制限
    if (!isLengthValid(merchantName, 100)) {
      setMerchantNameErrorMsg(getMessage("KGRERR058", 100).label);
      return false;
    }

    setMerchantNameErrorMsg("");
    return true;
  };

  //サービス名　バリデーションチェック
  const checkserviceName = (serviceName: any): boolean => {
    //必須
    if (isNull(serviceName.value)) {
      setServiceNameErrorMsg(getMessage("KGRERR055").label);
      return false;
    } else {
      setServiceNameErrorMsg("");
      return true;
    }
  };

  // 登録・更新前　バリデーションチェック
  const checkRateAdjustmentCase = (): boolean => {
    let validationCheck: boolean = true;

    if (!checkMerchantName(merchantName)) {
      validationCheck = false;
    }
    if (!checkserviceName(serviceName)) {
      validationCheck = false;
    }
    return validationCheck;
  };

  // 登録処理
  const registerRateAdjustmentCase = (): void => {
    const data: RateAdjustmentCaseMerchantRequest = {
      rateAdjustmentCaseNo: rateAdjustmentCaseNo as string,
      merchantBranchNo: "",
      merchantName: merchantName,
      contractServiceCode: serviceName.value,
    };
    postRateAdjustmentCaseMerchantRegister(data).then((result: any) => {
      if (!("error" in result) && !result.data.status.includes("error")) {
        navigate(`/rate-adjustment-case/${rateAdjustmentCaseNo}`);
      }
    });
  };
  // 更新処理
  const updateRateAdjustmentCase = (): void => {
    const data: RateAdjustmentCaseMerchantRequest = {
      rateAdjustmentCaseNo: rateAdjustmentCaseNo as string,
      merchantBranchNo: merchantBranchNo as string,
      merchantName: merchantName,
      contractServiceCode: serviceName.value,
    };
    putRateAdjustmentCaseMerchantUpdate(data).then((result: any) => {
      if (!("error" in result) && !result.data.status.includes("error")) {
        navigate(`/rate-adjustment-case/${rateAdjustmentCaseNo}`);
      }
    });
  };
  // 削除処理
  const deleteRateAdjustmentCase = (): void => {
    const data: RateAdjustmentCaseMerchantRequest = {
      rateAdjustmentCaseNo: rateAdjustmentCaseNo as string,
      merchantBranchNo: merchantBranchNo as string,
      merchantName: merchantName,
      contractServiceCode: serviceName.value,
    };
    deleteRateAdjustmentCaseMerchant(data).then((result: any) => {
      if (!("error" in result)) {
        navigate(`/rate-adjustment-case/${rateAdjustmentCaseNo}`);
      }
    });
  };

  //登録ボタン
  const onClickRegister = () => {
    if (!checkRateAdjustmentCase()) {
      return;
    } else {
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.DL,
          content: COMMON_LABELS.CONTENTS.REGISTER_CONFIRM,
          positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
          negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
          positiveFn: () => {
            registerRateAdjustmentCase();
          },
        })
      );
    }
  };
  //更新ボタン　API作成完了後また修正
  const onClickUpdate = () => {
    if (!checkRateAdjustmentCase()) {
      return;
    } else {
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.DL,
          content: COMMON_LABELS.CONTENTS.UPDATE_CONFIRM,
          positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
          negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
          positiveFn: () => {
            updateRateAdjustmentCase();
          },
        })
      );
    }
  };
  //削除ボタン　API作成完了後また修正
  const onClickDelete = () => {
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: COMMON_LABELS.CONTENTS.DELETE_CONFIRM,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveFn: () => {
          deleteRateAdjustmentCase();
        },
      })
    );
  };

  //戻るボタン　一覧画面完了後修正
  const onClickReturn = () => {
    if (!isFormEntered) {
      navigate(`/rate-adjustment-case/${rateAdjustmentCaseNo}`);
    } else {
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.DL,
          content: COMMON_LABELS.CONTENTS.PAGE_MOVE_CONFIRM,
          positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
          negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
          positiveFn: () => {
            navigate(`/rate-adjustment-case/${rateAdjustmentCaseNo}`);
          },
        })
      );
    }
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <Form
        actions={<SpaceBetween direction="horizontal" size="xs"></SpaceBetween>}
      >
        <SpaceBetween size="l">
          <SpaceBetween direction="vertical" size="l">
            <MerchantForm
              merchantName={merchantName}
              merchantNameErrorMsg={merchantNameErrorMsg}
              setMerchantName={setMerchantName}
              checkMerchantName={checkMerchantName}
              serviceName={serviceName}
              setServiceName={setServiceName}
              serviceNameList={serviceNameList}
              checkserviceName={checkserviceName}
              serviceNameErrorMsg={serviceNameErrorMsg}
              setIsFormEntered={setIsFormEntered}
              isRegister={isRegister}
              updatedBy={updatedBy}
              updatedAt={updatedAt}
            />
            <ColumnLayout columns={2}>
              <Box float="left">
                <Button
                  formAction="none"
                  variant="link"
                  onClick={onClickReturn}
                >
                  {COMMON_LABELS.BTNS.BACK}
                </Button>
              </Box>
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xxl">
                  {!isRegister ? (
                    <Button onClick={onClickDelete}>
                      {COMMON_LABELS.BTNS.DELETE}
                    </Button>
                  ) : (
                    ""
                  )}
                  <Button
                    variant="primary"
                    onClick={isRegister ? onClickRegister : onClickUpdate}
                  >
                    {isRegister
                      ? COMMON_LABELS.BTNS.REGISTER
                      : COMMON_LABELS.BTNS.UPDATE}
                  </Button>
                </SpaceBetween>
              </Box>
            </ColumnLayout>
          </SpaceBetween>
        </SpaceBetween>
      </Form>
    </form>
  );
};
export default Main;
